import {
  deprovisionEndpoint,
  getEndpointUrl,
  hoursPerMonth,
  selectEndpointById,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { endpointDetailSettingsUrl, endpointsUrl } from "@app/routes";
import { schema } from "@app/schema";
import { capitalize } from "@app/string-utils";
import type { DeployEndpoint, ProvisionableStatus } from "@app/types";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Box } from "../box";
import { ButtonDestroy } from "../button";
import { ExternalLink } from "../external-link";
import {
  IconAlertTriangle,
  IconCheckCircleAlt,
  IconInfo,
  IconScaleCheck,
  IconSettings,
  IconTrash,
  IconX,
} from "../icons";
import { Input } from "../input";
import { Pill } from "../pill";
import { formatSavingsCurrency } from "../scale-recs";
import { StatusText } from "../status-text";

export const formatSharedSavings = (monthlySavings: number): string =>
  `Save ${formatSavingsCurrency(monthlySavings)}/mo.`;

export const SharedEndpointCell = ({ enp }: { enp: DeployEndpoint }) => {
  const rates = useSelector(schema.costRates.select);
  const navigate = useNavigate();
  if (enp.shared) {
    return <Pill icon={<IconScaleCheck variant="sm" />}>Enabled</Pill>;
  }
  if (enp.platform !== "alb" || isWildcard(getEndpointUrl(enp)))
    return <span>N/A</span>;
  const monthlySavings =
    (rates.vhost_cost_per_hour - rates.shared_vhost_cost_per_hour) *
    hoursPerMonth;
  return (
    <div>
      <button
        type="button"
        className="cursor-pointer"
        onClick={() => navigate(endpointDetailSettingsUrl(enp.id))}
      >
        <Pill variant="progress">Available</Pill>
      </button>
      <div className="text-green-400 text-xs mt-1">
        {formatSharedSavings(monthlySavings)}
      </div>
    </div>
  );
};

export const EndpointStatusDisplay = ({
  status,
}: {
  status: ProvisionableStatus | "deprovisioned";
}) => {
  if (
    status === "provisioning" ||
    status === "deprovisioning" ||
    status === "pending"
  ) {
    return (
      <StatusText
        className="text-brown"
        icon={<IconSettings variant="sm" color="currentColor" />}
      >
        {capitalize(status)}
      </StatusText>
    );
  }

  if (
    status === "deprovision_failed" ||
    status === "provision_failed" ||
    status === "deprovisioned"
  ) {
    return (
      <StatusText
        className="text-red"
        icon={<IconX variant="sm" color="currentColor" />}
      >
        {capitalize(status.replace("_", " "))}
      </StatusText>
    );
  }

  if (status === "provisioned") {
    return (
      <StatusText
        className="text-forest"
        icon={<IconCheckCircleAlt variant="sm" color="currentColor" />}
      >
        Provisioned
      </StatusText>
    );
  }

  return (
    <StatusText
      className="text-indigo"
      icon={<IconInfo variant="sm" color="currentColor" />}
    >
      Unknown
    </StatusText>
  );
};

export const isWildcard = (url: string) => {
  return url.startsWith("*");
};

export function EndpointUrl({ enp }: { enp: DeployEndpoint }) {
  const url = getEndpointUrl(enp);

  if (enp.status === "provisioning") {
    return <p className="text-gray-500 leading-normal">Pending</p>;
  }

  if (
    !isWildcard(url) &&
    (enp.type === "http" || enp.type === "http_proxy_protocol")
  ) {
    return (
      <div className="leading-normal">
        <ExternalLink
          className="text-black group-hover:text-indigo hover:text-indigo"
          href={`https://${url}`}
          variant="default"
        >
          {url}
        </ExternalLink>
      </div>
    );
  }

  return <p className="leading-normal">{url}</p>;
}

export const EndpointDeprovision = ({
  envId,
  endpointId,
}: { endpointId: string; envId: string }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const action = deprovisionEndpoint({ id: endpointId });
  const loader = useLoader(action);
  const [confirm, setConfirm] = useState("");
  useLoaderSuccess(loader, () => {
    navigate(endpointsUrl());
  });
  const enp = useSelector((s) => selectEndpointById(s, { id: endpointId }));
  const url = getEndpointUrl(enp);
  const invalid = confirm !== url;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (invalid) return;
    dispatch(action);
  };

  return (
    <Box className="mb-8">
      <h1 className="text-lg text-red-500 font-semibold flex items-center gap-2 mb-4">
        <IconAlertTriangle color="#AD1A1A" />
        Deprovision Endpoint
      </h1>
      <form onSubmit={onSubmit}>
        <p>
          This will permanently deprovision <strong>{url}</strong> endpoint.
          This action cannot be undone. If you want to proceed, type the{" "}
          <strong>{url}</strong> below to continue.
        </p>
        <div className="flex mt-4">
          <Input
            className="flex-1"
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            id="delete-confirm"
          />
          <ButtonDestroy
            type="submit"
            envId={envId}
            variant="delete"
            isLoading={loader.isLoading}
            disabled={invalid}
            className="h-15 w-60 mb-0 ml-4 flex"
          >
            <IconTrash color="#FFF" className="mr-2" />
            Deprovision Endpoint
          </ButtonDestroy>
        </div>
      </form>
    </Box>
  );
};
